
<template>
  <div :class="{ 'blur-background': isMapShown }">
    <Card class="cardClass">
      <Toolbar class="subheading toolbar-with-z-index" v-click-outside="closeSearchBarHandler">
        <h4>Plant List</h4>
        <v-spacer></v-spacer>
        <TextInput
          :searchQuery="search"
          @search-submit="searchSubmitHandler"
          @filter-change="filterOptionChangeHandler"
          @search-input-change="searchChangeHandler"
        />
        <v-btn
          class="mapButton"
          @click="showMapHandler"
          v-show="$vuetify.breakpoint.mdAndUp"
        >
          {{ $t("View map") }}
          <img src="@/assets/locationicon.svg" alt="map" class="location" />
        </v-btn>
        <div class="legend-background">
          <v-btn class="home--datatable_legend" color="error" fab small />
          <span>{{ $t("New alert(s)") }}</span>
          <v-btn class="home--datatable_legend" color="success" fab small />
          <span>{{ $t("No alert(s)") }}</span>
        </div>
        <AvatarButton
          icon="get_app"
          :tip="$t('Download CSV')"
          class="hidden-sm-and-down"
          @click="download"
        />
      </Toolbar>
    </Card>
    <Card>
      <MobileList :data="data" v-if="isMobile" @alert-action="redirectAlert" />
      <DataTable
        v-else
        :headers="header"
        :data="data"
        :loading="dataLoading"
        @row-clicked="selectPlant"
        @alert-action="redirectAlert"
      />
    </Card>
    <v-divider></v-divider>
    <div class="text-xs-center pt-2">
      <v-layout class="paginationBox" justify-end>
        <v-flex shrink class="d-flex align-center pt-0 pb-0">
          <span class="primaryText--text mr-2">
            {{ itemFrom }} - {{ itemTo }} of {{ getTotalPlantCount }}
          </span>
          <span class="action d-flex align-center">
            <v-btn
              avatar
              fab
              small
              :disabled="!hasPrevious"
              class="fab-btn-xs mr-1"
              @click="previous"
            >
              <v-icon>navigate_before</v-icon>
            </v-btn>
            <v-btn avatar fab small :disabled="!hasNext" class="fab-btn-xs" @click="next">
              <v-icon>navigate_next</v-icon>
            </v-btn>
          </span>
        </v-flex>
      </v-layout>
    </div>
  </div>
</template>

<script>
import { mixin, mapGettersMixin } from "@/util/mixin/index";
import { mapGetters, mapMutations, mapActions } from "vuex";
import TextInput from "@/components/home/TextInput.vue";
import AvatarButton from "@/components/home/AvatarButton.vue";
import MobileList from "@/components/home/DataTable/MobileList.vue";
import DataTable from "@/components/home/DataTable/Table.vue";
import MapComponent from "@/components/home/MapComponent.vue";
import moment from "moment";

export default {
  mixins: [mixin, mapGettersMixin],
  props: {
    isMapShown: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    TextInput,
    AvatarButton,
    MobileList,
    DataTable,
    MapComponent,
  },
  data() {
    return {
      pagination: {
        rowsPerPage: 15,
      },
      filter: "",
      search: "",
      page: 1,
      data: [],
      param: {
        plantList: [],
      },
      dataLoading: true,
      timer: "",
      isExpanded: true,
      plantSearched: []
    };
  },
  mounted() {
    this.setIntervalWithoutDelay(this.callAllApi);
  },
  computed: {
    ...mapGetters([
      "getTotalPlantCount",
      "getPlantList",
      "getRules",
      "getSelectedPlantId",
    ]),
    noOfPage() {
      return Math.ceil(this.getTotalPlantCount / this.pagination.rowsPerPage);
    },
    hasPagination() {
      return this.noOfPage > 1;
    },
    plantListForCurrentPage() {
      const startIndex = this.pagination.rowsPerPage * (this.page - 1);
      const endIndex = startIndex + this.pagination.rowsPerPage;
      if (this.search === "") {
        return this.getPlantList.slice(startIndex, endIndex).map((el) => el.id);
      } else {
        return this.plantSearched.slice(startIndex, endIndex);
      }
    },
    isMobile() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
        case "sm":
          return true;
        case "md":
        case "lg":
        case "xl":
          return false;
      }
    },
    header() {
      let head = [
        {
          text: this.$t("Plant name"),
          align: "left",
          value: "plant_name",
          width: "300px",
          class: "primary",
        },
        {
          text: this.$t("City/ State/ Country"),
          value: "location",
          class: "primary",
        },
        {
          text: this.$t("Energy (kWh)"),
          value: "generation",
          align: "left",
          class: "primary header-text",
        },
        {
          text: this.$t("AC Power (kW)"),
          value: "power",
          align: "left",
          class: "primary",
        },
        {
          text: this.$t("PR (%)"),
          value: "ratio",
          align: "left",
          class: "primary",
        },
        {
          text: this.$t("AC Capacity (kW)"),
          value: "ac_capacity",
          align: "left",
          class: "primary",
        },
        {
          text: this.$t("Status"),
          sortable: false,
          value: "alert",
          align: "left",
          class: "primary",
        },
      ];
      return head;
    },
    hasPrevious() {
      return this.page > 1;
    },
    hasNext() {
      return this.page < this.noOfPage;
    },
    itemFrom() {
      return (this.page - 1) * this.pagination.rowsPerPage + 1;
    },
    itemTo() {
      return Math.min(this.page * this.pagination.rowsPerPage, this.getTotalPlantCount);
    },
  },
  watch: {
    isReload() {
      if (this.isReload) {
        this.setIntervalWithoutDelay(this.callAllApi);
      }
    },
    page() {
      this.todayGenerationDetail();
    },
    search() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.page = 1;
        this.debounce();
      }, 1500);
    },
  },
  methods: {
    ...mapActions(["fetchGenerationData", "fetchActivePlantDetails"]),
    ...mapMutations(["SET_SELECTED_COMPONENT_MENU", "SET_LAST_CALL_TIME", "SET_PROGRESS_BAR"]),
    closeSearchBarHandler() {
      this.isExpanded = !this.isExpanded
    },
    searchSubmitHandler(payload) {
      this.searchChangeHandler(payload.query)
      this.filterOptionChangeHandler(payload.filter)
      this.filterPlantHandler()
    },
    filterOptionChangeHandler(payload) {
      this.filter = payload
    },
    searchChangeHandler(payload) {
      this.search = payload
      this.filterPlantHandler()
    },
    filterPlantHandler() {
      this.plantSearched = this.getPlantList
        .filter((el) => {
          if (this.filter == 'plant') {
            return el.name.toLowerCase().includes(this.search.toLowerCase());
          } else if (this.filter == 'location') {
            return el.location.toLowerCase().includes(this.search.toLowerCase());
          } else {
            return el.name.toLowerCase().includes(this.search.toLowerCase()) || el.location.toLowerCase().includes(this.search.toLowerCase());
          }
        })
        .map((el) => el.id);
    },
    async debounce() {
      await this.todayGenerationDetail();
    },
    showMapHandler() {
      this.$emit("isMapShownStatus", true);
    },
    callAllApi() {
      let updateTime = moment().format("MM-DD-YYYY H:mm:ss");
      this.SET_LAST_CALL_TIME(updateTime);
      this.todayGenerationDetail();
    },
    async redirectAlert({ plantId }) {
      await this.fetchActivePlantDetails(plantId);
      this.$router.push({ name: "alert" });
    },
    todayGenerationDetail: async function () {
      if (this.plantListForCurrentPage.length === 0) {
        this.data.splice(0);
        return;
      }

      this.dataLoading = true;
      this.SET_PROGRESS_BAR(true);
      this.fetchGenerationData(this.plantListForCurrentPage).then((response) => {
        this.dataLoading = false;
        this.SET_PROGRESS_BAR(false);
        if (response.status == 200) {
          this.data = response.data.message.map((item) => {
            const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "N/A");
            return {
              ...item,
              location: `${capitalize(item.city)} / ${capitalize(item.location)} / ${capitalize(item.country)}`,
            };
          });
        }
      }).catch(err => {
        this.SET_PROGRESS_BAR(false)
        console.log(err, 'ERROR in fetching generation')
      });
    },
    refresh() {
      this.data.length = 0;
      this.setIntervalWithoutDelay(this.callAllApi);
    },
    async selectPlant(item) {
      const plantId = item.plant_id;
      await this.fetchActivePlantDetails(plantId);
      this.SET_SELECTED_COMPONENT_MENU("plantOverview");
      const path = this.getRules(["rule", "access_config"])
        ? this.getRules(["rule", "access_config"])["plantOverview"].path
        : "/plantOverview";
      this.$router.push(path);
    },
    download() {
      const dataCopy = _.cloneDeep(this.data);
      if (dataCopy.length === 0) {
      } else {
        for (let i = 0; i < dataCopy.length; i++) {
          dataCopy[i][`grid`] = dataCopy[i].alert
            ? dataCopy[i].alert.grid
              ? dataCopy[i].alert.grid
              : "NA"
            : "NA";
          dataCopy[i][`communication`] = dataCopy[i].alert
            ? dataCopy[i].alert.communication
              ? dataCopy[i].alert.communication
              : "NA"
            : "NA";
          dataCopy[i][`inverter`] = dataCopy[i].alert
            ? dataCopy[i].alert.inverter
              ? dataCopy[i].alert.inverter
              : "NA"
            : "NA";
          dataCopy[i][`scb`] = dataCopy[i].alert
            ? dataCopy[i].alert.scb
              ? dataCopy[i].alert.scb
              : "NA"
            : "NA";
          if (dataCopy[i].ratio >= 100) {
            dataCopy[i].ratio = "--";
          } else if (dataCopy[i].ratio == null) {
            dataCopy[i].ratio = "No data";
          }
        }
        this.JSONToCSVConvertor(dataCopy, "Plant Report", false);
      }
    },
    JSONToCSVConvertor(JSONData, ReportTitle, ShowLabel) {
      // If JSONData is not an object then JSON.parse will parse the JSON string in an Object
      var arrData = typeof JSONData !== "object" ? JSON.parse(JSONData) : JSONData;

      var CSV = "";
      // Set Report title in first row or line

      let header =
        "Plant name, City/ State/ Country, Energy (kWh), AC Power (kW),AC Capacity (kW), PR (%), Grid, Comm., Inverter, SMB";
      let headerOrder = [
        "plant_name",
        "location",
        "generation",
        "power",
        "ac_capacity",
        "ratio",
        "grid",
        "communication",
        "inverter",
        "scb",
      ];
      // This condition will generate the Label/Header
      if (ShowLabel) {
        let row = "";

        // This loop will extract the label from 1st index of on array
        for (let index in arrData[0]) {
          // Now convert each value to string and comma-seprated
          row += index + ",";
        }
        row = row.slice(0, -1);

        // append Label row with line break
        CSV += row + "\r\n";
      }
      CSV += header + "\r\n";

      // 1st loop is to extract each row
      for (var i = 0; i < arrData.length; i++) {
        let row = "";
        // 2nd loop will extract each column and convert it in string comma-seprated
        for (var index in headerOrder) {
          row += '"' + arrData[i][headerOrder[index]] + '",';
        }
        row.slice(0, row.length - 1);

        // add a line break after each row
        CSV += row + "\r\n";
      }

      if (CSV === "") {
        alert("Invalid data");
        return;
      }

      // Generate a file name
      var fileName = "MyReport_";
      // this will remove the blank-spaces from the title and replace it with an underscore
      fileName += ReportTitle.replace(/ /g, "_");

      // Initialize file format you want csv or xls
      var uri = "data:text/csv;charset=utf-8," + escape(CSV);

      // Now the little tricky part.
      // you can use either>> window.open(uri);
      // but this will not work in some browsers
      // or you will not get the correct file extension

      // this trick will generate a temp <a /> tag
      var link = document.createElement("a");
      link.href = uri;

      // set the visibility hidden so it will not effect on your web-layout
      link.style = "visibility:hidden";
      link.download = fileName + ".csv";

      // this part will append the anchor tag and remove it after automatic click
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    previous() {
      if (this.page > 1) this.page -= 1;
    },
    next() {
      if (this.page < this.noOfPage) this.page += 1;
    },
  },
};
</script>

<style scoped>
.toolbar-with-z-index {
  position: relative; 
   z-index: 3; /* This z-index is for search bar filter option to come above the header*/
}
.home--datatable_legend {
  height: 12px !important;
  width: 12px !important;
}

.cardClass {
  border-radius: 10px 10px 0px 0px;
}

.fab-btn-xs {
  height: 20px;
  width: 20px;
}

.paginationBox {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
.title {
  padding: 0px 20px;
}

@media (max-width: 960px) {
  .paginationBox {
    padding-bottom: 50px;
  }
}

.action {
  display: inline-flex;
  align-items: center;
}

.primaryText--text {
  font-size: 12px;
  font-weight: 500;
}

.action {
  display: flex;
  align-items: center;
}
</style>

<style>
.con-tooltip {
  background: #cc535673;
  cursor: pointer;
}

.renew-msg {
  margin-bottom: 6px;
  margin-top: 6px;
}

.tooltip {
  visibility: hidden;
  z-index: 1;
  opacity: 0.7;

  width: 20%;
  padding: 0px 20px;
  border: 2px solid #cc535673;
  background: #cc535673;
  color: white;

  position: absolute;
  top: -149%;
  left: 15%;

  border-radius: 9px;
  font: 16px;

  transform: translateY(9px);
  transition: all 0.3s ease-in-out;

  box-shadow: 0 0 3px rgba(56, 54, 54, 0.86);
}

.tooltip::after {
  content: " ";
  width: 0;
  height: 0;

  border-style: solid;
  border-width: 12px 12.5px 0 12.5px;
  border-color: #cc5356 transparent transparent transparent;

  position: absolute;
  left: 40%;
}

.con-tooltip:hover .tooltip {
  visibility: visible;
  transform: translateY(-10px);
  transition: 0.3s linear;
  animation: odsoky 1s ease-in-out infinite alternate;
}

@keyframes odsoky {
  0% {
    transform: translateY(6px);
  }

  100% {
    transform: translateY(1px);
  }
}

/*hover ToolTip*/
.top:hover {
  transform: translateY(0px);
}

.plant-datatable {
  max-height: 600px;
  overflow: auto;
  display: flex;
}

.theme--dark .plant-datatable th {
  z-index: 1;
  position: sticky;
  top: 0;
}

.theme--light .plant-datatable th {
  z-index: 1;
  position: sticky;
  top: 0;
}

/* Theme */
.blur-background {
  filter: blur(1px);
  transition: filter 0.3s ease;
}
.location {
  width: 15px;
  height: 15px;
  padding-left: 5px;
}

.mapButton {
  text-transform: none !important;
  font-size: 14px !important;
  color: var(--v-extraGrey-base);
  border-radius: 5px;
  border: 1px solid #213974 !important;
  height: 35px;
}

.theme--dark .mapButton {
  background-color: var(--v-primary-base) !important;
}

.theme--light .mapButton {
  background-color: var(--v-primary-base) !important;
}

.legend-background span {
  color: var(--v-extraGrey-base);
  font-size: 14px;
  font-weight: 500;
}

@media (max-width: 510px) {
  .legend-background span {
    font-size: 12px;
  }
}
</style>
